import {
  ButtonWithArrows,
  ButtonWithIcon,
  ColorPicker,
  InfiniteSelect,
  PopupContext,
  SectionToggle,
  ToggleButton,
} from 'components';
import { Formik, Form as FormikForm, useField as formikUseField, useFormikContext } from 'formik';
import { capitalize, isEmpty, throttle, uniqBy } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Form as BootstrapForm, Button, Col, InputGroup, Row } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import Flatpickr from 'react-flatpickr';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import CreatableSelect from 'react-select/creatable';
import MDEditor from '@uiw/react-md-editor';
import { toastr } from 'utils';
import { markdownEditorCommands, markdownEditorExtraCommands } from 'utils/markdownEditorCommands';
import { markdownPreviewOptions } from 'utils/markdownPreviewOptions';
import {
  API_DATE_FORMAT,
  API_DATE_TIME_FORMAT,
  FLATPICKR_API_DATE_FORMAT,
  FLATPICKR_API_DATE_TIME_FORMAT,
  FLATPICKR_DISPLAY_DATE_FORMAT,
  FLATPICKR_DISPLAY_DATE_TIME_FORMAT,
} from 'utils/date';

import 'react-image-crop/dist/ReactCrop.css';

import { VisibilityOff } from '@material-ui/icons';
import Visibility from '@material-ui/icons/Visibility';
import EntityService from 'api/services/EntityService';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import withDefaultProps from 'helpers/withDefaultProps';
import {
  Dropzone,
  ErrorFeedback,
  FilePreviewThumbnail,
  InputDescription,
  PreviewThumbnailWrapper,
  RadioCardCheckedIcon,
  RadioCardDescription,
  RadioCardHeading,
  RadioCardLabel,
  Checkbox as StyledCheckbox,
  Input as StyledInput,
  MarkdownEditor as StyledMarkdownEditor,
  RadioButton as StyledRadioButton,
} from './styles';

const animatedComponents = makeAnimated();

const FormContext = React.createContext();

let timeout;
const cleanupFunction = () => clearTimeout(timeout);
const HANDLE_SUBMIT_TIMEOUT = 600;

function useField(props) {
  const fieldProps = formikUseField(props);
  const form = useFormikContext();
  fieldProps[1].touched = fieldProps[1].touched || form.submitCount > 0;
  return fieldProps;
}

const Form = ({
  children,
  notifyOnFormErrors,
  renderDefaultButtons = true,
  submitLabel = 'Submit',
  extraButtons,
  pristine = false,
  style,
  className,
  ...restProps
}) => (
  <FormContext.Provider value={{ notifyOnFormErrors }}>
    <Formik validateOnMount enableReinitialize {...restProps}>
      {(formikProps) => (
        <FormikForm style={style} className={className}>
          {typeof children === 'function' ? children(formikProps) : children}

          {typeof extraButtons === 'function' ? extraButtons({ ...formikProps }) : extraButtons}

          {renderDefaultButtons && (
            <Form.Submit
              className="mt-3 text-uppercase"
              size="lg"
              variant="primary"
              pristine={pristine}
            >
              {submitLabel}
            </Form.Submit>
          )}
        </FormikForm>
      )}
    </Formik>
  </FormContext.Provider>
);

Form.Group = ({ children, style, ...restProps }) => (
  <BootstrapForm.Group
    style={{ position: 'relative', marginBottom: '1.375rem', ...style }}
    {...restProps}
  >
    {children}
  </BootstrapForm.Group>
);

Form.Label = ({ children, tooltipText, ...restProps }) => (
  <BootstrapForm.Label {...restProps} style={{ display: 'flex', alignItems: 'center' }}>
    {children}
    {tooltipText && <InfoTooltip title={tooltipText} color="#ced4da" iconSize={18} />}
  </BootstrapForm.Label>
);

Form.CreatableSelect = function CreatableMultiSelect({ ...props }) {
  const { popupRoot } = useContext(PopupContext);
  const [field, meta, { setValue }] = useField(props);
  const isInvalid = meta.touched && meta.error;

  const createOption = (label) => ({
    label,
    value: label.toLowerCase().replaceAll(/\W/g, ''),
  });

  const [inputValue, setInputValue] = React.useState('');

  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        setValue([...field.value, createOption(inputValue)]);
        setInputValue('');
        event.preventDefault();
        break;
      default:
        break;
    }
  };
  const selectCustomStyle = isInvalid ? { border: '1px solid red' } : {};

  return (
    <>
      <CreatableSelect
        {...field}
        inputValue={inputValue}
        isClearable
        isMulti
        menuIsOpen={false}
        onChange={(newValue) => {
          setValue(newValue);
        }}
        onInputChange={(newValue) => {
          setInputValue(newValue);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Type something and press enter..."
        styles={{
          control: (provided) => ({ ...provided, ...selectCustomStyle }),
        }}
        menuPortalTarget={popupRoot || undefined}
      />

      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.MultiSelect = function MultiSelect({
  options,
  isMulti = true,
  children,
  disabled = false,
  withSelectAll = false,
  ...props
}) {
  const { popupRoot } = useContext(PopupContext);
  const [field, meta, { setValue }] = useField(props);
  const isInvalid = meta.touched && meta.error;

  const onChange = (selectedOptions) => {
    if (isMulti) {
      if (selectedOptions?.find((option) => option.value === 'all')) {
        setValue(options.map((option) => option.value));
        return;
      }
      setValue(selectedOptions?.map((option) => option.value) || []);
    } else {
      setValue(selectedOptions?.value);
    }
  };

  const getValue = () =>
    isMulti
      ? options.filter((option) => field.value?.indexOf(option.value) >= 0)
      : options.filter((option) => field.value === option.value);

  const selectCustomStyle = isInvalid ? { border: '1px solid red' } : {};
  const disabledStyle = disabled ? { border: 'none', backgroundColor: 'transparent' } : {};

  const value = getValue();
  const menuPortalTarget =
    popupRoot || (typeof document !== 'undefined' ? document.body : undefined);
  if (!value.length && disabled) {
    return null;
  }

  return (
    <>
      <Select
        {...field}
        closeMenuOnSelect
        components={animatedComponents}
        options={withSelectAll ? [{ label: 'Select All', value: 'all' }, ...options] : options}
        isMulti={isMulti}
        isDisabled={disabled}
        menuPortalTarget={menuPortalTarget}
        onChange={onChange}
        value={value}
        isOpen
        styles={{
          control: (provided) => ({
            ...provided,
            ...selectCustomStyle,
            ...disabledStyle,
            minHeight: '44.39px',
            height: 'auto',
            textAlign: 'left',
          }),
          valueContainer: (provided) => ({
            ...provided,
            maxHeight: '150px',
            overflowY: 'auto',
            flexWrap: 'wrap',
          }),
          multiValue: (provided) => ({
            ...provided,
            maxWidth: '100%',
          }),
          dropdownIndicator: (provided) => (disabled ? { display: 'none' } : provided),
          indicatorSeparator: (provided) => (disabled ? { display: 'none' } : provided),
          multiValueRemove: (provided) => (disabled ? { display: 'none' } : provided),
          menu: (provided) => ({
            ...provided,
            zIndex: 1032,
            overflow: 'visible',
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 1032,
          }),
          option: (styles, { data }) => {
            if (withSelectAll && data.value === 'all') {
              return { ...styles, fontWeight: 'bold' };
            }
            return styles;
          },
        }}
        className={`text-left ${isInvalid && 'is-invalid'}  ${withSelectAll && '.select-all'}`}
        {...props}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.MultiSelectInfinite = function MultiSelectInfinite({
  options,
  initialOption,
  hasMoreOptions,
  loadMoreOptions,
  isLoadingOptions,
  isLoadingMoreOptions,
  onSearch,
  disabled = false,
  children,
  ...props
}) {
  const { popupRoot } = useContext(PopupContext);
  const [field, meta, { setValue }] = useField(props);

  const isInvalid = meta.touched && meta.error;

  const mergedOptions = useMemo(() => {
    if (!initialOption) {
      return options;
    }

    return [initialOption, ...options.filter((option) => option.value !== initialOption.value)];
  }, [options, initialOption]);

  const onChange = (selectedOptions) => setValue(selectedOptions?.value);

  const getValue = () => mergedOptions.filter((option) => field.value === option.value);

  const selectCustomStyle = isInvalid ? { border: '1px solid red' } : {};
  const disabledStyle = disabled ? { border: 'none', backgroundColor: 'transparent' } : {};

  const value = getValue();

  if (!value.length && disabled) {
    return null;
  }

  return (
    <>
      <InfiniteSelect
        options={mergedOptions}
        value={value}
        onChange={onChange}
        onSearch={onSearch}
        isLoadingOptions={isLoadingOptions}
        isLoadingMoreOptions={isLoadingMoreOptions}
        hasMoreOptions={hasMoreOptions}
        loadMoreOptions={loadMoreOptions}
        disabled={disabled}
        menuPortalTarget={popupRoot || undefined}
        styles={{
          control: (provided) => ({
            ...provided,
            ...selectCustomStyle,
            ...disabledStyle,
            height: '44.39px',
            textAlign: 'left',
          }),
          dropdownIndicator: (provided) => (disabled ? { display: 'none' } : provided),
          indicatorSeparator: (provided) => (disabled ? { display: 'none' } : provided),
          multiValueRemove: (provided) => (disabled ? { display: 'none' } : provided),
          menu: (provided) => ({
            ...provided,
            zIndex: 1032,
            overflow: 'visible',
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 1032,
          }),
          option: (styles) => styles,
        }}
        className={`text-left ${isInvalid && 'is-invalid'}`}
        {...props}
      >
        {children}
      </InfiniteSelect>
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

let requestId = 0;

Form.MultiSelectSalon = function MultiSelectSalon({ disabled = false, ...props }) {
  const [field, meta, { setValue }] = useField('location');
  const selectedIds = field.value?.map(({ id }) => id) || [];
  const isInvalid = meta.touched && meta.error;

  const [locations, setLocations] = React.useState(field.value || []);
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);

  const onChange = (selectedOptions) => {
    const newValue =
      selectedOptions?.map((option) => locations.find(({ id }) => option.value === id)) || [];
    setValue(newValue);
  };

  const locationsSelect = locations.map(({ label, id }) => ({
    label,
    value: id,
  }));
  const selectedLocations = locationsSelect.filter(({ value }) => selectedIds.indexOf(value) >= 0);
  const onInputChange = (search) => {
    if (!search) {
      return;
    }
    requestId += 1;

    setMenuIsOpen(true);
    // TODO: error handling
    const fetchLocations = async (fetchRequestId) => {
      const response = await EntityService.fetch('location', undefined, { search });
      const locationsPage = response?.results || [];
      const locationsOptions = locationsPage.map(
        ({ name: locationName, sap_id, uid, id, owners }) => ({
          label: `${locationName} - ${sap_id}`,
          uid,
          id,
          owners,
        }),
      );
      const previousLocations = locations.filter(({ id }) => selectedIds.indexOf(id) >= 0);
      const newLocations = uniqBy(previousLocations.concat(locationsOptions), ({ id }) => id);
      if (fetchRequestId !== requestId) {
        // new fetch has been started
        return;
      }
      setLocations(newLocations);
    };
    fetchLocations(requestId);
  };
  const onInputChangeRef = useRef(onInputChange);
  onInputChangeRef.current = onInputChange;

  const onInputChangeThrottled = useMemo(
    () => throttle((search) => onInputChangeRef.current(search), 300),
    [],
  );

  const selectCustomStyle = isInvalid ? { border: '1px solid red' } : {};
  const disabledStyle = disabled ? { border: 'none', backgroundColor: 'transparent' } : {};

  const onFocus = () => {
    if (locationsSelect.length) {
      setMenuIsOpen(true);
    }
  };

  return (
    <>
      <Select
        {...field}
        filterOption={() => true} // this makes all the difference - filtering occurs on server side
        closeMenuOnSelect={false}
        components={animatedComponents}
        placeholder="type to search"
        options={locationsSelect}
        isMulti
        isDisabled={disabled}
        onChange={onChange}
        menuIsOpen={menuIsOpen}
        onFocus={onFocus}
        onBlur={() => setMenuIsOpen(false)}
        onInputChange={onInputChangeThrottled}
        value={selectedLocations}
        styles={{
          control: (provided) => ({ ...provided, ...selectCustomStyle, ...disabledStyle }),
          indicatorsContainer: (provided) => ({
            ...provided,
            display: locationsSelect.length ? 'flex' : 'none',
          }),
          indicatorSeparator: (provided) => (disabled ? { display: 'none' } : provided),
          multiValueRemove: (provided) => (disabled ? { display: 'none' } : provided),
        }}
        className={`text-left ${isInvalid && 'is-invalid'}`}
        {...props}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.DatePicker = function DatePicker({ options = {}, ...props }) {
  const { popupRoot } = useContext(PopupContext);
  const [field, meta, { setValue }] = useField(props);

  const isInvalid = meta.touched && meta.error;

  const onChange = (date) => {
    setValue(moment(date[0]).format(API_DATE_FORMAT));
  };

  return (
    <>
      <Flatpickr
        {...field}
        {...props}
        placeholder="DD/MM/YYYY"
        options={{
          dateFormat: FLATPICKR_API_DATE_FORMAT,
          altInput: true,
          altFormat: FLATPICKR_DISPLAY_DATE_FORMAT,
          appendTo: popupRoot || undefined,
          ...options,
        }}
        className={`form-control ${isInvalid && 'is-invalid'}`}
        value={field.value}
        onChange={onChange}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.DateTimePicker = function DateTimePicker({ options = {}, ...props }) {
  const [field, meta, { setValue }] = useField(props);
  const { t } = useTranslation(['app']);
  const isInvalid = meta.touched && meta.error;

  const onChange = (date) => {
    setValue(moment(date[0]).format(API_DATE_TIME_FORMAT));
  };

  const minDate = moment().subtract(3, 'days').format(API_DATE_FORMAT);

  return (
    <>
      <Flatpickr
        name={field.name}
        {...props}
        placeholder={t('app:dateTimePlaceholder') || 'DD/MM/YYYY HH:mm'}
        options={{
          dateFormat: FLATPICKR_API_DATE_TIME_FORMAT,
          altInput: true,
          altFormat: FLATPICKR_DISPLAY_DATE_TIME_FORMAT,
          enableTime: true,
          time_24hr: true,
          minDate,
          ...options,
        }}
        className={`form-control ${isInvalid && 'is-invalid'}`}
        value={field.value}
        onChange={onChange}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.DateRangePicker = function DateRangePicker({ relativePeriods, ...props }) {
  const [field, meta, { setValue }] = useField(props);
  const ref = useRef();
  const { t } = useTranslation(['app']);

  const dateFormat = API_DATE_FORMAT;

  const startDate = useRef(
    moment().subtract(relativePeriods[0].durationInDays, 'days').format(dateFormat),
  );
  const endDate = useRef(moment().format(dateFormat));

  const isInvalid = meta.touched && meta.error;

  const onChange = ([date], type) => {
    let sd;
    let ed;

    if (type === 'start') {
      sd = moment(date);
      ed = moment(endDate.current);
    } else {
      sd = moment(startDate.current);
      ed = moment(date);
    }

    const diff = ed.diff(sd, 'days');

    const period = relativePeriods.find((p) => p.durationInDays === diff);

    if (period && moment().diff(ed, 'days') === 0) {
      ref.current.value = period.durationInDays;
    } else {
      ref.current.value = 0;
    }

    startDate.current = sd.format(dateFormat);
    endDate.current = ed.format(dateFormat);
    setValue({ endDate: ed.format(dateFormat), startDate: sd.format(dateFormat) });
  };

  useEffect(() => {
    setValue({ startDate: startDate.current, endDate: endDate.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // set default period based on passed relativePeriods

  const handlePeriodChange = (e) => {
    const ed = moment();

    const sd = moment().subtract(e.target.value, 'days');
    setValue({ startDate: sd.format(dateFormat), endDate: ed.format(dateFormat) });
  };

  return (
    <div className="d-flex flex-column">
      <BootstrapForm.Control
        className="custom-select"
        as="select"
        onChange={handlePeriodChange}
        isInvalid={isInvalid}
        ref={ref}
      >
        {relativePeriods.map(({ durationInDays, label }) => (
          <option key={durationInDays} value={durationInDays}>
            {label}
          </option>
        ))}
        <option value={0} hidden>
          {t('app:customPeriod')}
        </option>
      </BootstrapForm.Control>
      <Flatpickr
        placeholder="DD/MM/YYYY"
        options={{
          dateFormat: FLATPICKR_API_DATE_FORMAT,
          altInput: true,
          altFormat: FLATPICKR_DISPLAY_DATE_FORMAT,
          maxDate: moment().format(dateFormat),
        }}
        className={`form-control ${isInvalid && 'is-invalid'} mt-2`}
        value={field.value.startDate}
        onChange={(date) => onChange(date, 'start')}
      />
      <Flatpickr
        placeholder="DD/MM/YYYY"
        options={{
          dateFormat: FLATPICKR_API_DATE_FORMAT,
          altInput: true,
          altFormat: FLATPICKR_DISPLAY_DATE_FORMAT,
          maxDate: moment().format(dateFormat),
        }}
        className={`form-control ${isInvalid && 'is-invalid'} mt-2`}
        value={field.value.endDate}
        onChange={(date) => onChange(date, 'end')}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </div>
  );
};

Form.ColorPicker = function PickColorInput({ ...props }) {
  const [field, meta, { setValue }] = useField(props);
  const { t } = useTranslation(['app']);

  const [isPickerComponentVisible, setVisibility] = useState(false);

  const isInvalid = meta.touched && meta.error;

  const onChange = (color) => {
    setValue(color);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex' }}>
        <Form.Input
          type="text"
          {...props}
          placeholder={t('app:pickColor')}
          onFocus={() => setVisibility(true)}
          showErrorFeedback={false}
        />
        <button
          type="button"
          onClick={() => setVisibility(true)}
          style={{
            backgroundColor: field.value,
            border: '1px solid  #d2ddec',
            width: 50,
            borderRadius: '0.375rem',
            marginLeft: 3,
          }}
        />
      </div>

      {isPickerComponentVisible && (
        <div style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
          <ColorPicker onChange={onChange} onHide={() => setVisibility(false)} />
        </div>
      )}
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </div>
  );
};

Form.Input = function Input({
  append = '',
  prepend = '',
  description = '',
  validate,
  children,
  mask = false,
  showErrorFeedback = true,
  ...props
}) {
  const [field, meta] = useField({ ...props, validate });

  const isInvalid = meta.touched && meta.error;

  if (append || prepend) {
    return (
      <InputGroup className="w-100">
        <StyledInput mask={mask ? 1 : 0}>
          {prepend && (
            <InputGroup.Text className="rounded-0 rounded-left border-right-0">
              {prepend}
            </InputGroup.Text>
          )}
          <BootstrapForm.Control
            sm={10}
            {...field}
            {...props}
            isInvalid={isInvalid}
            className={`rounded-0 ${
              prepend && append ? '' : prepend ? 'rounded-right' : 'rounded-left'
            }`}
          />
          {append && (
            <InputGroup.Text className="rounded-0 rounded-right border-left-0">
              {append}
            </InputGroup.Text>
          )}
        </StyledInput>
        {!!description.length && <InputDescription>{description}</InputDescription>}
        {showErrorFeedback && isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
      </InputGroup>
    );
  }

  return (
    <>
      <StyledInput mask={mask ? 1 : 0}>
        <BootstrapForm.Control sm={10} {...field} {...props} isInvalid={isInvalid} />
      </StyledInput>
      {!!description.length && <InputDescription>{description}</InputDescription>}

      {showErrorFeedback && isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.MarkdownEditor = function MarkdownEditor({
  description = '',
  height = 200,
  maxLength,
  placeholder,
  validate,
  ...props
}) {
  const [field, meta, { setTouched, setValue }] = useField({ ...props, validate });
  const isInvalid = meta.touched && meta.error;

  return (
    <>
      <StyledMarkdownEditor>
        <MDEditor
          className={isInvalid ? 'is-invalid' : undefined}
          commands={markdownEditorCommands}
          data-color-mode="light"
          extraCommands={markdownEditorExtraCommands}
          height={height}
          value={field.value || ''}
          onChange={(markdown = '') => setValue(markdown)}
          previewOptions={markdownPreviewOptions}
          placeholder={placeholder}
          textareaProps={{
            maxLength,
            name: field.name,
            onBlur: () => setTouched(true),
          }}
        />
      </StyledMarkdownEditor>
      {!!description.length && <InputDescription>{description}</InputDescription>}
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.Password = function Password({ children, ...props }) {
  const [field, meta] = useField(props);
  const [showPassword, setShowPassword] = React.useState(false);
  const isInvalid = meta.touched && meta.error;

  return (
    <InputGroup>
      <BootstrapForm.Control
        sm={10}
        type={showPassword ? 'text' : 'password'}
        {...field}
        {...props}
        isInvalid={isInvalid}
      />
      <InputGroup.Text
        onClick={() => setShowPassword((prev) => !prev)}
        className="h-100 rounded-0 rounded-right border-left-0"
        style={{ cursor: 'pointer' }}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </InputGroup.Text>
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </InputGroup>
  );
};

Form.Select = function SelectInput({ options, disabled = false, ...props }) {
  const { onChange: parentOnChange, ...restProps } = props;
  const [field, meta, { setValue, setTouched }] = useField(restProps);
  const isInvalid = meta.touched && meta.error;

  const onChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setValue(value);
    if (parentOnChange) {
      parentOnChange(selectedOption);
    }
  };

  const selectCustomStyle = isInvalid ? { border: '1px solid red' } : {};
  const disabledStyle = disabled ? { border: 'none', backgroundColor: 'transparent' } : {};

  const value = options.find((option) => option.value === field.value);

  return (
    <>
      <Select
        {...field}
        value={value}
        closeMenuOnSelect
        onChange={onChange}
        onBlur={() => setTouched(true)}
        options={options}
        isDisabled={disabled}
        styles={{
          control: (provided) => ({
            ...provided,
            ...selectCustomStyle,
            ...disabledStyle,
            minHeight: '44.39px',
            textAlign: 'left',
          }),
          dropdownIndicator: () => ({ display: 'none' }),
          indicatorSeparator: () => ({ display: 'none' }),
          option: (styles) => styles,
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        className={`text-left ${isInvalid && 'is-invalid'}`}
        menuPortalTarget={document.body}
        {...restProps}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.FileUpload = function FileUpload({ maxFiles = 6, accept = 'image/*', ...props }) {
  const { t } = useTranslation('app');
  const [field, meta, { setValue, setTouched }] = useField(props);
  const isInvalid = meta.touched && meta.error;
  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxFiles,
    onDrop: async (acceptedFiles) => {
      const currentFiles = Array.isArray(field.value)
        ? field.value
        : field.value
          ? [field.value]
          : [];
      const nextValue =
        currentFiles.length === maxFiles
          ? [...currentFiles.slice(1), ...acceptedFiles]
          : [...currentFiles, ...acceptedFiles];

      await setValue(nextValue);
      await setTouched(true);
    },
  });

  const handleFileDelete = (file) =>
    setValue(field.value.filter((item) => item.name !== file.name));

  return (
    <>
      <Dropzone {...getRootProps({ refKey: 'innerRef' })}>
        <input {...getInputProps()} disabled={props.disabled} />
        <p className="text-muted text-center">
          {maxFiles === 1 ? t('dropzonePlaceholderSingle') : t('dropzonePlaceholder')}
        </p>
      </Dropzone>
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
      {field.value?.length ? (
        <PreviewThumbnailWrapper>
          {field.value.map((file) => (
            <FilePreviewThumbnail>
              {!props.disabled && (
                <button
                  type="button"
                  className="btn-white rounded-circle"
                  disabled={props.disabled}
                  onClick={() => handleFileDelete(file)}
                >
                  <i className="fe fe-x" />
                </button>
              )}
              <div className="flex-1">
                <h4>{file?.name}</h4>
              </div>
            </FilePreviewThumbnail>
          ))}
        </PreviewThumbnailWrapper>
      ) : null}
    </>
  );
};

Form.Checkbox = function Checkbox({ children, ...props }) {
  const [field, meta] = useField(props);

  const isInvalid = meta.touched && meta.error;

  return (
    <>
      <StyledCheckbox
        type="checkbox"
        checked={field.value}
        {...field}
        {...props}
        isInvalid={isInvalid}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

Form.RadioButton = function RadioButton({ children, ...props }) {
  const [field, meta] = useField(props);
  const { label } = props;
  const { handleMouseOver, handleMouseOut, value, name } = props;
  const { fieldValue } = field;
  const { values } = useFormikContext();
  const isInvalid = meta.touched && meta.error;

  return (
    <div
      className="d-flex"
      onMouseOver={handleMouseOver}
      onFocus={handleMouseOver}
      onMouseOut={handleMouseOut}
      onBlur={handleMouseOut}
    >
      <StyledRadioButton
        id={`option-${fieldValue}`}
        label={label}
        type="radio"
        {...field}
        {...props}
        isInvalid={isInvalid}
        checked={values[name] === value}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </div>
  );
};

Form.RadioCard = function RadioCard({ heading, description, children, ...props }) {
  const [field] = useField(props);
  const { handleMouseOver, handleMouseOut, onChange, value, name, style } = props;

  const { values } = useFormikContext();
  const isChecked = values[name] === value;

  return (
    <RadioCardLabel
      htmlFor={`option-${value}`}
      onMouseOver={handleMouseOver}
      onFocus={handleMouseOver}
      onMouseOut={handleMouseOut}
      onBlur={handleMouseOut}
      $isChecked={isChecked}
      style={style}
    >
      <input
        type="radio"
        id={`option-${value}`}
        {...field}
        name={name}
        value={value}
        checked={isChecked}
        onChange={onChange}
        className="visually-hidden"
      />
      {isChecked && <RadioCardCheckedIcon className="fe fe-check-circle" />}
      {heading && <RadioCardHeading>{heading}</RadioCardHeading>}
      {description && <RadioCardDescription>{description}</RadioCardDescription>}
      {children}
    </RadioCardLabel>
  );
};

Form.RadioGroup = function RadioGroup({ children, ...props }) {
  const [, meta] = useField(props);
  const isInvalid = meta.touched && meta.error;

  return (
    <div>
      {children}
      {isInvalid && <ErrorFeedback className="text-right">{meta.error}</ErrorFeedback>}
    </div>
  );
};

Form.MultiCheckbox = function MultiCheckbox({ options, ...props }) {
  const [field, meta, { setValue }] = useField(props);
  const { t } = useTranslation(['app']);
  const isInvalid = meta.touched && meta.error;

  const all = 'all';

  useEffect(() => {
    if (options.length > 1 && options.length === field.value.length)
      setValue([...field.value, all]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueSelection = (value) => {
    if (value === all) {
      if (options.every((opt) => field.value.includes(opt.value))) {
        setValue([]);
      } else {
        setValue([...options.map((opt) => opt.value), all]);
      }
    } else if (field.value.includes(value)) {
      setValue(field.value.filter((v) => v !== value && v !== all));
    } else if (field.value.length === options.length - 1 && !field.value.includes(all)) {
      setValue([...field.value, value, all]);
    } else {
      setValue([...field.value, value]);
    }
  };

  return (
    <>
      {options.length > 1 && (
        <div className="d-flex">
          <StyledCheckbox
            type="checkbox"
            checked={field.value.includes('all')}
            onChange={() => handleValueSelection('all')}
          />
          <div>{t('app:selectAll')}</div>
        </div>
      )}
      {options.map(({ value, label }) => (
        <div className="d-flex">
          <StyledCheckbox
            type="checkbox"
            checked={field.value.includes(value)}
            onChange={() => handleValueSelection(value)}
            {...props}
            isInvalid={isInvalid}
          />
          <div>{label}</div>
        </div>
      ))}
      {isInvalid && <ErrorFeedback>{meta.error}</ErrorFeedback>}
    </>
  );
};

Form.DependentField = function DependentField({
  dependencyName,
  calculateOptions,
  Component,
  initialOption = undefined,
  ...props
}) {
  const [{ value }, { touched }] = useField(dependencyName);
  const filteredValues = Array.isArray(value) ? value.filter((v) => v !== 'all') : value;
  const [options, setOptions] = useState(initialOption || calculateOptions(filteredValues));

  useEffect(() => {
    setOptions(calculateOptions(filteredValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched]);

  return <Component options={options} {...props} />;
};

Form.Toggle = function Checkbox({ ...props }) {
  const [field, meta] = useField(props);
  const isInvalid = meta.touched && meta.error;

  const handleOnChange = (e) => {
    field.onChange(e);

    if (props.onChange) props?.onChange(e);
  };

  return (
    <ToggleButton
      checked={field.value}
      {...field}
      {...props}
      onChange={handleOnChange}
      isInvalid={isInvalid}
    />
  );
};

Form.DictionaryField = function DictionaryField({ children, withToggle, ...props }) {
  const [field, meta, { setValue }] = useField(props);
  const [numberOfInputs, setNumberOfInputs] = useState(field.value.length);
  const { t } = useTranslation(['app']);
  const isInvalid = meta.touched && meta.error;

  return (
    <FormikForm>
      <BootstrapForm.Control
        key={`${props.name}[0]`}
        sm={10}
        {...field}
        {...props}
        name={`${props.name}[0]`}
        value={field.value[0]}
        isInvalid={isInvalid}
      />
      {numberOfInputs > 1 &&
        (withToggle ? (
          <SectionToggle
            trigger={(isOpen) => (
              <Button variant="link" className="w-100 d-flex justify-content-center pb-0">
                <p className="text-small m-0">{isOpen ? t('showLess') : t('showMore')}</p>
                <i className={`fe fe-chevron-${isOpen ? 'up' : 'down'}`} />
              </Button>
            )}
            defaultOpen
          >
            {Array.from(new Array(numberOfInputs - 1).keys()).map((_, i) => (
              <BootstrapForm.Control
                key={`${props.name}[${i + 1}]`}
                sm={10}
                {...field}
                {...props}
                name={`${props.name}[${i + 1}]`}
                value={field.value[i + 1]}
                isInvalid={isInvalid}
                className="mb-3"
              />
            ))}
          </SectionToggle>
        ) : (
          Array.from(new Array(numberOfInputs - 1).keys()).map((_, i) => (
            <BootstrapForm.Control
              key={`${props.name}[${i + 1}]`}
              sm={10}
              {...field}
              {...props}
              name={`${props.name}[${i + 1}]`}
              value={field.value[i + 1]}
              isInvalid={isInvalid}
              className="mt-3"
            />
          ))
        ))}
      {!props.disabled && (
        <Row>
          <Col>
            {numberOfInputs > 1 && (
              <button
                type="button"
                onClick={() => {
                  if (numberOfInputs === field.value.length) {
                    setValue(field.value.slice(0, -1));
                  }
                  setNumberOfInputs(numberOfInputs - 1);
                }}
                className="btn btn-link p-0 btn-sm"
              >
                Remove last
              </button>
            )}
          </Col>
          <Col className="text-right">
            <button
              type="button"
              onClick={() => setNumberOfInputs(numberOfInputs + 1)}
              className="btn btn-link p-0 btn-sm"
            >
              Add more
            </button>
          </Col>
        </Row>
      )}
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </FormikForm>
  );
};

export const getLabel = (label, tooltipText, required) => {
  const capitalizedLabel = capitalize(label);

  return (
    <Form.Label tooltipText={tooltipText}>
      {capitalizedLabel}
      {required && <span style={{ color: 'red' }}>*</span>}
    </Form.Label>
  );
};

Form.GroupVertical = ({ label, children, required, tooltipText, ...rest }) => (
  <Form.Group {...rest}>
    <Row className="align-items-center">
      <Col className="d-flex align-items-center" lg="4">
        {getLabel(label, tooltipText, required)}
      </Col>
      <Col className="text-right" lg="8">
        {children}
      </Col>
    </Row>
  </Form.Group>
);

Form.GroupHorizontal = ({ label, children, required, style, tooltipText, ...rest }) => (
  <Form.Group style={style} {...rest}>
    {getLabel(label, tooltipText, required)}
    {children}
  </Form.Group>
);

Form.Submit = function Submit({
  children,
  animated,
  icon,
  onClick,
  areFormDependenciesReady = true,
  skipSubmitAction = false,
  withArrows,
  pristine = false,
  ignoreClickedState = false,
  isLoading = false,
  ...restProps
}) {
  const { handleSubmit, isValid, touched, validateForm, dirty } = useFormikContext();

  const [clicked, setClicked] = React.useState(false);
  const { notifyOnFormErrors } = React.useContext(FormContext);
  const { t } = useTranslation(['toastr']);

  const showSpinner = isLoading || clicked;

  const handleOnClick = async (e) => {
    e.preventDefault();
    setClicked(!ignoreClickedState);
    if (notifyOnFormErrors) {
      const errors = await validateForm();
      if (!isEmpty(errors)) {
        setClicked(false);
        toastr.error(t('toastr:errorsInForm'), '', {
          component: (
            <>
              {Object.keys(errors).map((item) => (
                <p className="text-small m-0">
                  <span className="text-danger">{item.toUpperCase()}</span> {errors[item]}
                </p>
              ))}
            </>
          ),
        });
      }
    }

    if (onClick) {
      setTimeout(() => {
        if (isValid && !isEmpty(touched)) {
          onClick();
        }
      }, HANDLE_SUBMIT_TIMEOUT);
    }
    if (!skipSubmitAction) {
      handleSubmit();
    }
  };

  useEffect(() => cleanupFunction, []);

  if (animated) {
    return (
      <ButtonWithIcon
        variant="primary"
        type="submit"
        text={children}
        onClick={handleOnClick}
        disabled={!isValid || showSpinner || (pristine && !dirty)}
        small
        className="text-uppercase"
        {...restProps}
      >
        <ConditionalSpinner showSpinner={showSpinner}>{icon}</ConditionalSpinner>
      </ButtonWithIcon>
    );
  }

  if (withArrows) {
    return (
      <ButtonWithArrows
        variant="primary"
        type="submit"
        text={children}
        onClick={handleOnClick}
        disabled={!isValid || showSpinner || (pristine && !dirty)}
        size="lg"
        className="text-uppercase"
        {...restProps}
      >
        <ConditionalSpinner showSpinner={showSpinner}>{children}</ConditionalSpinner>
      </ButtonWithArrows>
    );
  }

  return (
    <Button
      variant="primary"
      type="submit"
      onClick={handleOnClick}
      disabled={!isValid || showSpinner || !areFormDependenciesReady || (pristine && !dirty)}
      className="text-uppercase"
      {...restProps}
    >
      <ConditionalSpinner showSpinner={showSpinner}>{children}</ConditionalSpinner>
    </Button>
  );
};

/*       Proptypes        */

Form.propTypes = {
  notifyOnFormErrors: PropTypes.bool,
  submitLabel: PropTypes.string,
  extraButtons: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  pristine: PropTypes.bool,
  ...withDefaultProps(),
};

Form.Group.propTypes = { ...withDefaultProps() };

Form.Label.propTypes = { ...withDefaultProps() };

Form.Input.propTypes = {
  disabled: PropTypes.bool,
  showErrorFeedback: PropTypes.bool,
  ...withDefaultProps(),
};
Form.Toggle.propTypes = {
  onChange: PropTypes.func,

  ...withDefaultProps(),
};

Form.Password.propTypes = { ...withDefaultProps() };

Form.Checkbox.propTypes = {
  disabled: PropTypes.bool,
  ...withDefaultProps(),
};

Form.RadioButton.propTypes = {
  disabled: PropTypes.bool,
  ...withDefaultProps(),
};

Form.RadioCard.propTypes = {
  ...withDefaultProps(),
};

Form.RadioGroup.propTypes = {
  ...withDefaultProps(),
};

Form.MultiCheckbox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ...withDefaultProps(),
};

Form.DependentField.propTypes = {
  dependencyName: PropTypes.string.isRequired,
  calculateOptions: PropTypes.func.isRequired,
  Component: PropTypes.oneOf([Form.MultiSelect, Form.MultiCheckbox]),
  ...withDefaultProps(),
};

Form.DatePicker.propTypes = {
  options: PropTypes.object,
};

Form.DateTimePicker.propTypes = {
  options: PropTypes.object,
  ...withDefaultProps(),
};

Form.DateRangePicker.propTypes = {
  relativePeriods: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      durationInDays: PropTypes.number.isRequired,
    }),
  ),
};

Form.DictionaryField.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  ...withDefaultProps(),
};

Form.GroupVertical.propTypes = {
  label: PropTypes.string,
  children: PropTypes.any,
  required: PropTypes.bool,
  tooltipText: PropTypes.string,
};

Form.GroupHorizontal.propTypes = {
  label: PropTypes.string,
  children: PropTypes.any,
  required: PropTypes.bool,
  style: PropTypes.object,
  tooltipText: PropTypes.string,
};

Form.FileUpload.propTypes = {
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
};

Form.Submit.propTypes = {
  ...withDefaultProps(),
  animated: PropTypes.bool,
  icon: PropTypes.node,
  withArrows: PropTypes.bool,
  pristine: PropTypes.bool,
};

Form.Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,

  ...withDefaultProps(),
};

Form.MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,
  ...withDefaultProps(),
};

Form.MultiSelectSalon.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,
  ...withDefaultProps(),
};

export default Form;
